/**
 * Design tokens — the single source of truth for every color, radius, shadow,
 * spacing step, font size and breakpoint in the app.
 *
 * Consumed three ways, all derived from this file:
 *   1. `muiTheme.ts`      — builds DEFAULT_THEME for MUI components
 *   2. `cssVariables.ts`  — emits `--w-*` custom properties onto `:root`,
 *                           which SCSS reads as `var(--w-color-brand)`
 *   3. `_tokens.scss`     — generated (`yarn tokens:scss`) for the one thing
 *                           custom properties cannot do: `@media` queries
 *
 * Deliberately dependency-free plain data so it can be imported by the theme,
 * by hooks, and by build scripts without pulling in React or MUI.
 */

/**
 * Brand teal ramp. All four teals previously in use (#219FA8, #2C969D,
 * #1CB6C1, #17A8B2) were hue 184 differing only in saturation, so they were
 * visually interchangeable. `brand` is the canonical value; the rest of the
 * ramp is that same hue and saturation at different lightness.
 */
export const BRAND = {
  subtle: '#E5F9FA',
  light: '#5DD7DF',
  base: '#219FA8',
  dark: '#18757C',
} as const;

export const COLOR = {
  /** Primary text and the app's near-black. */
  ink: '#141414',
  inkMuted: '#555555',
  inkSubtle: '#888888',

  brandSubtle: BRAND.subtle,
  brandLight: BRAND.light,
  brand: BRAND.base,
  brandDark: BRAND.dark,

  /** Consolidates #ED7474 and #eb5757. */
  danger: '#ED7474',
  /** Deeper red used for destructive menu items (was #d32f2f). */
  dangerStrong: '#D32F2F',
  /** Soft red behind destructive warnings. */
  dangerSubtle: '#FEE2E2',
  /** Consolidates #FFD643, #FFD700, rgb(242 201 76) and the invalid 258-red. */
  accent: '#FFD643',
  info: '#62C5EF',

  /** Main page background behind the whole app. */
  page: '#FBFBFB',
  surface: '#FFFFFF',
  surfaceRaised: '#FAFAFA',
  /** Floating cards that sit over a map or photo. */
  surfaceFloating: '#FBFBFB',
  surfaceSunken: '#F5F5F5',
  surfaceHover: '#F0F0F0',
  /** Frosted surfaces for controls floating over photography. */
  surfaceTranslucent: 'rgb(255 255 255 / 90%)',
  surfaceTranslucentHover: 'rgb(255 255 255 / 95%)',

  border: '#E0E0E0',
  borderStrong: '#CCCCCC',
  borderSubtle: '#EBEBEB',

  /** Scrims and translucent overlays, in modern space-slash notation. */
  overlaySubtle: 'rgb(0 0 0 / 8%)',
  overlay: 'rgb(0 0 0 / 15%)',
  overlayStrong: 'rgb(0 0 0 / 70%)',

  /**
   * Retained only so the MUI palette keeps its current `secondary.main`.
   * @deprecated Collapse into `brand` during the color sweep, then delete.
   */
  legacyTeal: '#2C969D',
} as const;

/** Informal 4px-based step scale. Required for new code; existing px untouched. */
export const SPACE = {
  none: 0,
  xxs: 2,
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
  xxxl: 48,
} as const;

/**
 * `md` is the app's de-facto radius: 44 of 118 declarations already use 10px,
 * and it is what MuiButton and the global `button` rule both settled on. The
 * rest of the ramp covers the next most common values (4px x14, 8px x11,
 * 16px for menu surfaces).
 */
export const RADIUS = {
  none: 0,
  xs: 4,
  sm: 8,
  md: 10,
  lg: 16,
  pill: 999,
} as const;

export const SHADOW = {
  sm: '0 2px 8px rgb(0 0 0 / 10%)',
  md: '0 4px 12px rgb(0 0 0 / 15%)',
  lg: '0 4px 20px rgb(0 0 0 / 15%)',
  overlay: '0 8px 32px rgb(0 0 0 / 20%)',
  focused: '0 3px 3px rgb(0 0 0 / 25%)',
} as const;

/** rem-based so the browser's text-size preference is honored. */
export const FONT_SIZE = {
  xs: '0.75rem',
  sm: '0.875rem',
  md: '1rem',
  lg: '1.25rem',
  xl: '1.5rem',
} as const;

export const FONT_WEIGHT = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

export const FONT_FAMILY = "'Poppins', 'Helvetica Neue', Arial, sans-serif";

export const TRANSITION = {
  fast: '0.15s ease-in-out',
  base: '0.2s ease-in-out',
  slow: '0.3s ease-in-out',
} as const;

export const Z_INDEX = {
  base: 0,
  sticky: 10,
  mapOverlay: 400,
  drawer: 1200,
  modal: 1300,
  menu: 1400,
  toast: 1500,
} as const;

/**
 * The app has exactly one breakpoint. It was previously duplicated as a literal
 * `822px` across 25 SCSS files plus `useMobileMediaQuery`; this is now its only
 * definition. SCSS reads it from the generated `_tokens.scss` because custom
 * properties are not valid inside `@media`.
 */
export const BREAKPOINT = {
  mobileMax: 822,
} as const;

export const TOKENS = {
  color: COLOR,
  space: SPACE,
  radius: RADIUS,
  shadow: SHADOW,
  fontSize: FONT_SIZE,
  fontWeight: FONT_WEIGHT,
  transition: TRANSITION,
  zIndex: Z_INDEX,
  breakpoint: BREAKPOINT,
} as const;
